import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

const FilterSelect = ({ label, value, options, onChange, disabled }) => {
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const ref = useRef(null)

  const selected = options.find(o => String(o.id) === String(value)) || null

  useEffect(() => {
    if (!open) return
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const filtered = useMemo(() => {
    const t = term.trim().toLowerCase()
    return t ? options.filter(o => o.name.toLowerCase().includes(t)) : options
  }, [term, options])

  const pick = id => { onChange(id); setOpen(false); setTerm('') }

  return (
    <Wrap ref={ref}>
      <Trigger type='button' $active={!!selected} disabled={disabled} onClick={() => setOpen(o => !o)} aria-haspopup='listbox' aria-expanded={open}>
        <TriggerLabel>{selected ? selected.name : label}</TriggerLabel>
        {selected
          ? <ClearMark as='span' role='button' aria-label={`Clear ${label} filter`} onClick={e => { e.stopPropagation(); pick(null) }}><CloseRoundedIcon style={{ fontSize: 15 }} /></ClearMark>
          : <KeyboardArrowDownRoundedIcon style={{ fontSize: 18, opacity: 0.6 }} />}
      </Trigger>
      {open && (
        <Menu role='listbox'>
          <SearchRow>
            <SearchRoundedIcon style={{ fontSize: 16, color: '#94a3b8' }} />
            <input autoFocus value={term} onChange={e => setTerm(e.target.value)} placeholder={`Search ${label.toLowerCase()}…`} />
          </SearchRow>
          <Options>
            <Opt $selected={!value} onClick={() => pick(null)}>All {label.toLowerCase()}s</Opt>
            {filtered.map(o => (
              <Opt key={o.id} $selected={String(o.id) === String(value)} onClick={() => pick(o.id)}>{o.name}</Opt>
            ))}
            {filtered.length === 0 && <Empty>No matches</Empty>}
          </Options>
        </Menu>
      )}
    </Wrap>
  )
}

export default FilterSelect

const Wrap = styled.div`position: relative; display: inline-flex;`

const Trigger = styled.button`
  display: inline-flex; align-items: center; gap: 6px; max-width: 240px;
  border: 1px solid ${({ $active }) => ($active ? '#cbd5e1' : '#e2e8f0')};
  background: ${({ $active }) => ($active ? '#eef2f7' : '#fff')};
  color: ${({ $active }) => ($active ? '#1e293b' : '#475569')};
  padding: 6px 10px; border-radius: 999px; font-size: 13px; font-weight: 600; cursor: pointer;
  &:hover:not(:disabled) { border-color: #cbd5e1; color: #1e293b; }
  &:disabled { opacity: 0.55; cursor: default; }
`
const TriggerLabel = styled.span`white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`
const ClearMark = styled.span`display: inline-flex; align-items: center; color: #64748b; &:hover { color: #b00020; }`

const Menu = styled.div`
  position: absolute; top: calc(100% + 6px); left: 0; z-index: 30; width: 260px;
  background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; box-shadow: 0 12px 28px rgba(15,23,42,0.14);
  overflow: hidden;
`
const SearchRow = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 9px 12px; border-bottom: 1px solid #eef0f3;
  input { border: none; outline: none; font: inherit; font-size: 13.5px; width: 100%; background: transparent; }
`
const Options = styled.div`max-height: 240px; overflow-y: auto; padding: 4px;`
const Opt = styled.div`
  padding: 8px 10px; border-radius: 7px; font-size: 13.5px; cursor: pointer;
  color: ${({ $selected }) => ($selected ? '#1e293b' : '#334155')};
  font-weight: ${({ $selected }) => ($selected ? 700 : 500)};
  background: ${({ $selected }) => ($selected ? '#eef2f7' : 'transparent')};
  &:hover { background: #f1f5f9; }
`
const Empty = styled.div`padding: 12px 10px; font-size: 13px; color: #94a3b8; text-align: center;`
